import useSWR from "swr";
import { endpointsAssets } from "lib/actions/endpoints/endpointsAssets";
import { fetcher } from "lib/actions/fetcher";
import { useAssets } from "lib/hooks/assets/useAssets";
import { useCurrentProjectId } from "lib/hooks/useProject";
import { useAssetUploads } from "lib/hooks/assets/useAssetUploads";
import { AssetIngressModel } from "lib/types/assets/AssetIngressModel";
import { IngressListItemModel } from "./IngressListItemModel";

export function useIngressesAndUploads() {
  const projectId = useCurrentProjectId();
  const { data: ingresses } = useSWR<AssetIngressModel[]>(
    projectId && endpointsAssets.projectIngresses(projectId),
    fetcher,
    { refreshInterval: 10000 },
  );
  const { uploads } = useAssetUploads(projectId);
  const { versions } = useAssets(projectId);

  const ingressesWithUploads =
    ingresses
      ?.map<IngressListItemModel>((ingress) => {
        const upload = uploads?.find(
          (upload) => upload.id === ingress.id.toString(),
        );
        if (!upload && ingress.status === "uploading") {
          //ingress exists on the server but the upload no longer exists in this browser session
          return null;
        }

        return {
          id: ingress.id.toString(),
          ingressSource: ingress.source,
          name: upload?.file.name ?? ingress.name ?? "Unknown",
          uploadStatus: upload?.status ?? null,
          ingressStatus: ingress.status ?? "none",
          progress: upload?.progress,
          errorMessage: ingress.message,
          isDismissed: ingress.isDismissed,
        };
      })
      .filter((item) => !!item && !item.isDismissed) ?? [];

  //create ingress items for uploads that have not yet been persisted as ingresses on the server
  const uploadsWithoutIngresses =
    uploads
      ?.filter(
        (u) => !ingressesWithUploads.some((ingress) => ingress.id === u.id),
      )
      .map<IngressListItemModel>((upload) => {
        return {
          id: upload.id.toString(),
          ingressSource: "browserUpload",
          name: upload.file.name,
          uploadStatus: upload.status ?? null,
          ingressStatus: "none",
          progress: upload.progress,
          isDismissed: false,
        };
      }) ?? [];

  const ingressesAndUploads = [
    ...ingressesWithUploads,
    ...uploadsWithoutIngresses,
  ];

  return {
    ingressesAndUploads,
  };
}
